import { useInfiniteQuery, useQuery, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import { ApiProblemError } from '../../../lib/apiProblemError';
import { ALL_ELSE_UNEXCLUDABLE } from '@shared/flow/signal-chips';
import { FlowSignalsResponseSchema, SIGNAL_SYNTHETIC_GROUP_KEYS, SIGNAL_SYNTHETIC_SORT_KEYS, type FlowSignalsResponse } from '@shared/flow/signal-view';
import { SIGNAL_CLIENT_FILTER_KEYS } from '@shared/flow/filter-fields';
import { PriorityRankingEntrySchema, type PriorityRankingEntry } from '@shared/schemas/priority-ranking';
import type { ContactRow } from '@shared/flow/contact-view';
import type { FilterGroup, GroupLogic } from '@shared/flow/filter';
import { useFlowContacts } from '../contact/hooks';

/**
 * Signals tab queries (plan 2026-09-24-002 U5). Mirrors
 * `contact/hooks.ts`'s `useFlowContacts` template step for step — see that
 * file's own comments for the shared plumbing's rationale (keyset paging,
 * `staleTime: Infinity` + `keepPreviousData`). Both `queryFn`s below go
 * through `fetchJson` (`lib/apiFetchJson.ts`, plan
 * `2026-09-25-003-pr1683-premerge-fixes-plan.md` U3/U4a) instead of the old
 * `errorMessageFromResponse` + `validateResponse` pair — same fail-closed
 * schema check, one call instead of two, and a schema-mismatch now throws
 * (`ApiProblemError`, `kind: 'malformed'`) rather than logging and returning
 * the raw payload.
 */

export interface FlowSignalsParams {
  search: string;
  filters: FilterGroup[];
  groupLogic: GroupLogic;
  sort: { field: string; dir: 'ASC' | 'DESC' };
  /** Ordered grouping levels — the server levels are sent as `group_by=a,b,c` (`serverGroupByFor`). */
  groupBy?: string[];
  /**
   * Flow-admin "View as" (Signals V3 PR 3, U15): the viewed rep's Salesforce user id. The server
   * rewrites `{me}` ONLY — never impersonation. `null`/`undefined` omit the URL param.
   */
  viewAs?: string | null;
  /**
   * "All else": ask the server for My Signals minus the rows the viewer's favorites include
   * (`exclude_favorites=1`). Part of the query key, so on/off never share an entry.
   */
  excludeFavorites?: boolean;
  enabled?: boolean;
}

/**
 * D10 — Priority and Local time are computed in the browser (the Ranker
 * needs the full unfiltered/ungrouped page, and local time needs the rep's
 * own clock); the server 400s a sort/group_by naming either
 * (`flow-signals-list.ts`'s `rejectSyntheticSortOrGroup`). Sorting BY one of
 * them on the wire therefore rides the route's own default sort instead —
 * the client re-sorts the loaded page locally (`arrangeSignalsClientSide`,
 * driven from `SignalsFlowTable.tsx`).
 */
const SIGNAL_SYNTHETIC_SORT_FIELDS: ReadonlySet<string> = new Set(SIGNAL_SYNTHETIC_SORT_KEYS);
const SERVER_FALLBACK_SORT: { field: string; dir: 'ASC' | 'DESC' } = { field: 'event_date', dir: 'DESC' };

function serverSortFor(sort: { field: string; dir: 'ASC' | 'DESC' }): { field: string; dir: 'ASC' | 'DESC' } {
  return SIGNAL_SYNTHETIC_SORT_FIELDS.has(sort.field) ? SERVER_FALLBACK_SORT : sort;
}

const CLIENT_GROUP_KEYS: ReadonlySet<string> = new Set(SIGNAL_SYNTHETIC_GROUP_KEYS);

/**
 * QA batch 3, D-A step 2 — the grouping levels the SERVER groups by:
 * `groupBy` minus the client-computed `local_time` / `business_hours`
 * levels (`arrangeSignalsClientSide` groups those in the browser over the
 * fully loaded list). Used for both `group_by=` and the query key — a client
 * level does not change the request, so it must not refetch page 1 either.
 */
export function serverGroupByFor(groupBy: string[] | undefined): string[] {
  return (groupBy ?? []).filter(key => !CLIENT_GROUP_KEYS.has(key));
}

/**
 * knv.13 — the filter groups the SERVER evaluates: conditions on a
 * browser-evaluated field (`business_hours`, `clientFilters.ts`) are dropped,
 * and a group left with none disappears. Used for both `filters=` and the
 * query key — a client-only condition does not change the request, so it must
 * not refetch page 1 either (`useSignalTableModel`'s release key goes through
 * `flowSignalsQueryKey`, so both sides always agree).
 */
export function serverFiltersFor(filters: FilterGroup[]): FilterGroup[] {
  return filters.flatMap(group => {
    const conditions = group.conditions.filter(c => !SIGNAL_CLIENT_FILTER_KEYS.includes(c.field));
    return conditions.length > 0 ? [{ ...group, conditions }] : [];
  });
}

/** The list's query key — ONE builder, used by `useFlowSignals` and by the auto-loader's release
 *  (fix-plan-batch4.md D-A), so the two can never name different entries. Client levels are stripped
 *  (`serverGroupByFor`): adding or removing Local time / Business hours does not change the key. */
export function flowSignalsQueryKey(params: Omit<FlowSignalsParams, 'enabled'>) {
  return ['flow-signals', {
    search: params.search,
    filters: serverFiltersFor(params.filters),
    groupLogic: params.groupLogic,
    sort: params.sort,
    groupBy: serverGroupByFor(params.groupBy),
    serverSort: serverSortFor(params.sort),
    viewAs: params.viewAs ?? null,
    excludeFavorites: params.excludeFavorites ?? false,
  }] as const;
}

/**
 * Marks stale every list entry that excludes the viewer's favorites. Favorite and saved-view writes change what
 * "the rows your favorites include" means, so those entries (and only those) must refetch.
 */
export function invalidateExcludingSignalLists(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: ['flow-signals'],
    predicate: query => hasExcludeFavorites(query.queryKey[1]),
  });
}

function hasExcludeFavorites(keyMember: unknown): boolean {
  return typeof keyMember === 'object' && keyMember !== null
    && 'excludeFavorites' in keyMember && keyMember.excludeFavorites === true;
}

export type FlowSignalsQueryKey = ReturnType<typeof flowSignalsQueryKey>;

/** Keyset-paginated Signals list — every param change requeries page 1. */
/** The Signals list's 400 for `exclude_favorites=1` when a favorite filters on Open business hours (it can't be excluded). */
export function isUnexcludableFavoritesError(err: unknown): err is ApiProblemError {
  return err instanceof ApiProblemError && err.kind === 'http' && err.status === 400 && err.code === ALL_ELSE_UNEXCLUDABLE.code;
}

/**
 * The client's own retry policy (`main.tsx` sets 1), minus the deterministic rejection above: resending that 400 can
 * only fail the same way. `clientRetry` is the QueryClient's default `retry` (TanStack's own default is 3 when unset).
 */
export function signalsListRetry(failureCount: number, err: Error, clientRetry: QueryClientRetry = 1): boolean {
  if (isUnexcludableFavoritesError(err)) return false;
  if (typeof clientRetry === 'function') return clientRetry(failureCount, err);
  if (typeof clientRetry === 'number') return failureCount < clientRetry;
  return clientRetry === true;
}

type QueryClientRetry = NonNullable<NonNullable<ReturnType<QueryClient['getDefaultOptions']>['queries']>['retry']>;

export function useFlowSignals(params: FlowSignalsParams) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const serverSort = serverSortFor(params.sort);
  const serverGroupBy = serverGroupByFor(params.groupBy);
  const serverFilters = serverFiltersFor(params.filters);

  return useInfiniteQuery<FlowSignalsResponse, Error, InfiniteData<FlowSignalsResponse>, FlowSignalsQueryKey, string | null>({
    enabled: params.enabled ?? true,
    queryKey: flowSignalsQueryKey(params),
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams({
        sort: serverSort.field,
        dir: serverSort.dir,
        group_logic: params.groupLogic,
      });
      if (params.search.trim()) qs.set('search', params.search.trim());
      if (serverFilters.length > 0) qs.set('filters', JSON.stringify(serverFilters));
      if (serverGroupBy.length > 0) qs.set('group_by', serverGroupBy.join(','));
      if (params.viewAs) qs.set('viewAs', params.viewAs);
      if (params.excludeFavorites) qs.set('exclude_favorites', '1');
      if (typeof pageParam === 'string' && pageParam) qs.set('cursor', pageParam);

      return fetchJson(apiFetch, FlowSignalsResponseSchema, `/api/flow/signals?${qs.toString()}`, { timeoutMs: 30_000 });
    },
    initialPageParam: null as string | null,
    getNextPageParam: last => last.next_cursor,
    staleTime: Infinity,
    // Previous rows stay on screen only while the VIEWER is unchanged (a filter/sort change under the
    // same viewer); switching viewer restarts the list from blank so one viewer's rows never sit under another's state.
    // The same holds for "All else": rows from the other side of the exclusion are never shown under the chip
    // (or under its absence) while the replacement request is in flight.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1].viewAs === (params.viewAs ?? null) &&
      previousQuery.queryKey[1].excludeFavorites === (params.excludeFavorites ?? false)
        ? previous
        : undefined,
    retry: (failureCount, err) => signalsListRetry(failureCount, err, queryClient.getDefaultOptions().queries?.retry ?? 3),
    refetchOnWindowFocus: false,
    // Double-error-presentation fix: this error already renders inline
    // (`SignalsFlowTable` passes `error={query.error ? query.error.message :
    // null}` to `FlowTable`), so it must opt out of the global QueryCache
    // toast too. `useSignalsRanking` below deliberately does NOT set this —
    // its error has no inline surface today (`SignalsFlowTable` silently
    // falls back to an empty ranking list), so the global toast is the only
    // way a rep would ever learn the ranking fetch failed.
    meta: { suppressErrorToast: true },
  });
}

const SignalsRankingResponseSchema = z.object({ entries: z.array(PriorityRankingEntrySchema) });
export interface SignalsRankingResponse {
  entries: PriorityRankingEntry[];
}

/** The raw Ranker entries the browser needs to compute `priority` for a page of rows. */
export function useSignalsRanking() {
  const { apiFetch } = useApi();
  return useQuery<SignalsRankingResponse>({
    queryKey: ['flow-signals-ranking'],
    staleTime: Infinity,
    queryFn: () => fetchJson(apiFetch, SignalsRankingResponseSchema, '/api/flow/signals/ranking'),
  });
}

/**
 * Single-contact lookup for the Signals detail panel (bead .11's
 * `contactId` addition to `useFlowContacts`) — the real `ContactRow` a
 * signal's `contact_id` resolves to, never a synthesized/adapted one.
 */
export function useContactRow(contactId: string | null) {
  const query = useFlowContacts({
    contactId: contactId ?? undefined,
    search: '',
    filters: [],
    groupLogic: 'AND',
    sort: { field: 'name', dir: 'ASC' },
    enabled: !!contactId,
  });
  const row: ContactRow | null = query.data?.pages[0]?.rows[0] ?? null;
  return { ...query, row };
}

/**
 * Single-signal lookup for a `?signal=<id>` deep link whose row is NOT in the
 * current view (plan 2026-10-13-001 U1, decision 2). A side query on the
 * same list route with a one-condition `signal_id equals <id>` filter — never
 * a filter pushed into the view machine, which would persist as a working
 * filter + "Modified" banner. `useSignalTableModel` enables it only once the
 * list has loaded without the id, then pins the result as a chipped row.
 */
export function useSignalRow(signalId: string | null, opts: { enabled: boolean }) {
  const query = useFlowSignals({
    search: '',
    filters: [{ id: 'pin', logic: 'AND', conditions: [{ id: 'pin-1', field: 'signal_id', operator: 'equals', value: signalId }] }],
    groupLogic: 'AND',
    sort: { field: 'event_date', dir: 'DESC' },
    enabled: opts.enabled && !!signalId,
  });
  const row = query.data?.pages[0]?.rows[0] ?? null;
  return { ...query, row };
}
